import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IMAGES } from '../constants/theme';
import NewsLetter from '../elements/NewsLetter';
import PageTitle from '../elements/PageTitle';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const API_URL = `${API_BASE}/courses/Packages/healthy-packages-price`;

// Safe error message extractor to prevent circular reference errors
const getErrorMessage = (err) => {
  if (!err) return 'Unknown error occurred';
  
  // If it's already a string, return it
  if (typeof err === 'string') return err;
  
  // Try to get message property
  if (err.message && typeof err.message === 'string') return err.message;
  
  // Try to get error property if it's a response object
  if (err.error) {
    if (typeof err.error === 'string') return err.error;
    if (err.error.message && typeof err.error.message === 'string') return err.error.message;
  }
  
  // Try toString as last resort
  try {
    return err.toString();
  } catch (e) {
    return 'Unknown error occurred';
  }
};

const Pricing = () => {
  const navigate = useNavigate();
  const { packageId: urlPackageId } = useParams(); // Get package ID from URL
  const [hoverEffect, setHoverEffect] = useState(0);
  const [pricePlans, setPricePlans] = useState({});
  const [pricePlansOriginal, setPricePlansOriginal] = useState({});
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [processing, setProcessing] = useState(null); // Track which plan is being processed
  const [selectedPackageId, setSelectedPackageId] = useState(null); // Store selected healthy package ID
  const [pendingOrders, setPendingOrders] = useState([]); // Store pending orders from database

  // Helper function to check if promotion is still valid
  const isPromotionValid = (promo) => {
    if (!promo) return false;
    
    const now = new Date();
    
    // Check if promotion has started (valid_from)
    if (promo.valid_from) {
      const validFrom = new Date(promo.valid_from);
      if (now < validFrom) return false;
    }
    
    // Check if promotion has expired (valid_to)
    if (promo.valid_to) {
      const validTo = new Date(promo.valid_to);
      // Set time to end of day for valid_to comparison
      validTo.setHours(23, 59, 59, 999);
      if (now > validTo) return false;
    }
    
    return true;
  };

  useEffect(() => {
    async function fetchPricing() {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(API_URL, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) throw new Error("Failed to fetch pricing data");

        const data = await res.json();

        setPricePlans(data?.pricePlans || {});
        setPricePlansOriginal(data?.pricePlansOriginal || {});
        setPromotion(data?.promotion || null);

      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    // Fetch selected healthy package ID (from URL or first available)
    async function fetchSelectedPackage() {
      try {
        // If package ID is in URL, use it
        if (urlPackageId) {
          setSelectedPackageId(urlPackageId);
          return;
        }

        // Otherwise, fetch packages and use the first one
        const res = await fetch(`${API_BASE}/courses/Packages/healthy-packages`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.packages && data.packages.length > 0) {
            setSelectedPackageId(data.packages[0].id);
          }
        }
      } catch (err) {
        console.error("Error fetching package:", err);
      }
    }

    async function checkSubscription() {
      const token = localStorage.getItem("token");
      if (!token) {
        setHasSubscription(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/users/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const userData = await res.json();
          // Check if user has active subscription for healthy packages
          // Healthy packages have pack_id: null and may have package_id
          // Check subscriptions array for more accurate check
          if (userData.subscriptions && userData.subscriptions.length > 0) {
            const healthyPackageSub = userData.subscriptions.find(sub => 
              (!sub.pack_id || sub.pack_id === null) && 
              sub.status === 'active' &&
              sub.current_period_end &&
              new Date(sub.current_period_end) > new Date()
            );
            if (healthyPackageSub) {
              setHasSubscription(true);
              return;
            }
          }
          // Fallback to old subscription object for backward compatibility
          if (
            userData.subscription && 
            userData.subscription.status === 'active' &&
            (!userData.subscription.pack_id || userData.subscription.pack_id === null)
          ) {
            // Also check if subscription hasn't expired
            if (userData.subscription.current_period_end) {
              const endDate = new Date(userData.subscription.current_period_end);
              if (endDate > new Date()) {
                setHasSubscription(true);
                return;
              }
            }
          }
          setHasSubscription(false);
        }
      } catch (err) {
        console.error("Error checking subscription:", err);
        setHasSubscription(false);
      }
    }

    // Check for pending orders from database
    async function checkPendingOrders() {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await fetch(`${API_BASE}/courses/healthy/pay/pending`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          const pendingOrdersList = data.pendingOrders || [];
          
          // Check if order in localStorage still exists in pending list
          const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');
          if (pendingOrder.orderId) {
            const orderExists = pendingOrdersList.some(order => 
              order.orderId === pendingOrder.orderId
            );
            if (!orderExists) {
              // Order doesn't exist in pending list, likely failed or completed - clear it
              localStorage.removeItem('pendingPayPalOrder');
              console.log('Cleared localStorage - order no longer pending (likely failed or completed)');
            }
          }
          
          if (pendingOrdersList.length > 0) {
            setPendingOrders(pendingOrdersList);
            
            // If we have pending orders but no localStorage entry, try to restore
            if (!pendingOrder.orderId && pendingOrdersList[0]) {
              const firstPending = pendingOrdersList[0];
              // Store the pending order info for manual capture
              localStorage.setItem('pendingPayPalOrder', JSON.stringify({
                orderId: firstPending.orderId,
                packageId: firstPending.packageId,
                plan: firstPending.plan
              }));
            }
          } else {
            setPendingOrders([]);
          }
        }
      } catch (err) {
        console.error("Error checking pending orders:", err);
      }
    }

    fetchPricing();
    fetchSelectedPackage();
    checkSubscription();
    checkPendingOrders();
  }, [urlPackageId]);

  // Handle PayPal return - check URL params for token (PayPal order ID)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // PayPal can return orderId as 'token' or in other formats
    const token = urlParams.get('token') || urlParams.get('orderId');
    const PayerID = urlParams.get('PayerID');

  

    // Check if we have a pending order in localStorage
    const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');
    
    // If we have a token from PayPal and a pending order, try to capture
    if (token && pendingOrder.orderId) {
      // Use the orderId from pendingOrder (source of truth) instead of URL token
      handlePayPalReturn(pendingOrder.orderId);
    } else if (pendingOrder.orderId) {
     
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePayPalReturn = async (orderIdFromUrl) => {
    const token = localStorage.getItem("token");
    const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');

    if (!token) {
      alert("Please login to complete payment");
      navigate("/login");
      return;
    }

    // Use orderId from pendingOrder if available, otherwise use from URL
    const orderId = pendingOrder.orderId || orderIdFromUrl;

    if (!orderId) {
      console.error("No order ID found");
      alert("No order ID found. Please contact support.");
      return;
    }

    // If we have pending order, use its orderId (it's the source of truth)
    if (pendingOrder.orderId && pendingOrder.orderId !== orderIdFromUrl) {
      console.warn("Order ID mismatch. Using pending order ID:", pendingOrder.orderId);
    }

    try {
      setLoading(true);
      setError(null);
      
      
      // Get user ID from token
      const userRes = await fetch(`${API_BASE}/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!userRes.ok) {
        throw new Error("Failed to get user information");
      }

      const userData = await userRes.json();
      const userId = userData.user.id || userData.user._id;

      if (!userId) {
        throw new Error("User ID not found");
      }

     

      // Capture PayPal payment
      const res = await fetch(`${API_BASE}/courses/healthy/pay/capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: orderId,
          userId: userId,
          packageId: pendingOrder.packageId,
          plan: pendingOrder.plan
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        throw new Error("Failed to parse server response");
      }

      if (!res.ok) {
        // Check if order needs approval
        if (data?.error === 'ORDER_NOT_APPROVED' && data?.approvalUrl) {
          // Redirect to PayPal approval page
          window.location.href = data.approvalUrl;
          return;
        }
        const errorMsg = getErrorMessage(data?.error || data) || "Failed to capture payment";
        throw new Error(errorMsg);
      }


      // Clear pending order only if capture was successful
      if (data.alreadyCompleted) {
        console.log('Payment was already completed');
      }
      
      localStorage.removeItem('pendingPayPalOrder');

      
      // Refresh subscription status
      setHasSubscription(true);
      
      // Clear URL params to prevent re-triggering
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Redirect to healthy packages page
      navigate("/Healthy");
      
    } catch (err) {
      console.error("Error capturing PayPal payment:", err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      alert(`Payment Error: ${errorMsg}\n\nPlease try clicking "Complete Payment" button if available.`);
      setLoading(false);
    }
  };

  // Manual capture function (fallback if auto-capture fails)
  const handleManualCapture = async (orderId = null, packageId = null, plan = null) => {
    const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');
    
    // Use provided parameters or fall back to localStorage
    const finalOrderId = orderId || pendingOrder.orderId;
    const finalPackageId = packageId || pendingOrder.packageId;
    const finalPlan = plan || pendingOrder.plan;
    
    if (!finalOrderId) {
      // Try to get from pending orders from database
      if (pendingOrders.length > 0) {
        const firstPending = pendingOrders[0];
        await handlePayPalReturnWithDetails(
          firstPending.orderId,
          firstPending.packageId,
          'monthly' // Default to monthly, could be improved
        );
        return;
      }
      alert("No pending order found. Please start a new purchase.");
      return;
    }

    // If we have all details, use them
    if (finalPackageId && finalPlan) {
      await handlePayPalReturnWithDetails(finalOrderId, finalPackageId, finalPlan);
    } else {
      await handlePayPalReturn(finalOrderId);
    }
  };

  // Enhanced capture function with all details
  const handlePayPalReturnWithDetails = async (orderId, packageId, plan) => {
    const token = localStorage.getItem("token");

    if (!token) {
      alert("Please login to complete payment");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      
      // Get user ID from token
      const userRes = await fetch(`${API_BASE}/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!userRes.ok) {
        throw new Error("Failed to get user information");
      }

      const userData = await userRes.json();
      const userId = userData.user.id || userData.user._id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      // Capture PayPal payment
      const res = await fetch(`${API_BASE}/courses/healthy/pay/capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: orderId,
          userId: userId,
          packageId: packageId,
          plan: plan
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        throw new Error("Failed to parse server response");
      }

      if (!res.ok) {
        // Check if order needs approval
        if (data?.error === 'ORDER_NOT_APPROVED' && data?.approvalUrl) {
          // Redirect to PayPal approval page
          window.location.href = data.approvalUrl;
          return;
        }
        const errorMsg = getErrorMessage(data?.error || data) || "Failed to capture payment";
        throw new Error(errorMsg);
      }


      // Clear pending order
      localStorage.removeItem('pendingPayPalOrder');
      setPendingOrders([]);

      
      // Refresh subscription status
      setHasSubscription(true);
      
      // Clear URL params to prevent re-triggering
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Redirect to healthy packages page
      navigate("/Healthy");
      
    } catch (err) {
      console.error("Error capturing PayPal payment:", err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      alert(`Payment Error: ${errorMsg}`);
      setLoading(false);
    }
  };

  if (loading) return <div>Loading pricing...</div>;
  if (error) return <div>Error: {error}</div>;

  // Ensure data exists
  if (!pricePlans || Object.keys(pricePlans).length === 0) {
    return <div>No pricing data available.</div>;
  }

  // Dynamically generated plans
  const plans = [
    {
      id: "monthly",
      title: "1 Month",
      price: pricePlans.monthly,
      originalPrice: pricePlansOriginal.monthly,
      description: "Perfect for trying out our services for a short time.",
      features: [
        "Quick Start Guide",
        "Daily Workout Plans",
        "Basic Nutrition Tips",
        "Community Support Access",
        "Progress Tracking",
      ],
    },
    {
      id: "quarterly",
      title: "3 Months",
      price: pricePlans.quarterly,
      originalPrice: pricePlansOriginal.quarterly,
      description: "Ideal for a more comprehensive and steady transformation.",
      features: [
        "Comprehensive Training Program",
        "Work with Resources",
        "Weekly Coaching Sessions",
        "Advanced Progress Analytics",
        "Support & Mentoring",
      ],
    },
  ].filter(plan => plan.price); // Only show plans returned from backend

  // Handle plan selection and payment
  const handlePlanClick = async (planId) => {
    const token = localStorage.getItem("token");
    
    // Check if user is logged in
    if (!token) {
      if (window.confirm("You need to login to purchase a plan. Would you like to login now?")) {
        navigate("/login");
      }
      return;
    }

    // Note: We don't block purchases here based on subscription
    // The backend will check if the user already purchased THIS SPECIFIC package
    // Users can purchase different packages even if they have a subscription to another package

    // Check if we have a package ID
    if (!selectedPackageId) {
      alert("Package information is loading. Please try again in a moment.");
      return;
    }

    // If there are pending orders, inform user but allow retry (don't block)
    const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');
    // Check if order in localStorage exists in pending orders (backend only returns pending, not failed)
    const orderExists = pendingOrder.orderId && 
      pendingOrders.some(order => order.orderId === pendingOrder.orderId);
    if (orderExists || pendingOrders.length > 0) {
      // Just log - don't block the user from retrying
      console.log('Creating new payment attempt. Old pending payment will be marked as failed.');
    }

    try {
      setProcessing(planId);
      setError(null);

      // Clear old pending orders before creating new one
      localStorage.removeItem('pendingPayPalOrder');
      setPendingOrders([]);

      // Create PayPal order using /healthy/pay endpoint
      const res = await fetch(`${API_BASE}/courses/healthy/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          packageId: selectedPackageId,
          plan: planId 
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        throw new Error("Failed to parse server response");
      }

      if (!res.ok) {
        const errorMsg = getErrorMessage(data?.error || data) || "Failed to create payment order";
        throw new Error(errorMsg);
      }

      // Store orderId and packageId in localStorage for capture after PayPal return
      if (data.orderId) {
        localStorage.setItem('pendingPayPalOrder', JSON.stringify({
          orderId: data.orderId,
          packageId: selectedPackageId,
          plan: planId
        }));
        
        // Refresh pending orders list
        setTimeout(() => {
          // Re-check pending orders after a moment
          const checkPending = async () => {
            try {
              const pendingRes = await fetch(`${API_BASE}/courses/healthy/pay/pending`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              });
              if (pendingRes.ok) {
                const pendingData = await pendingRes.json();
                setPendingOrders(pendingData.pendingOrders || []);
              }
            } catch (err) {
              console.error("Error refreshing pending orders:", err);
            }
          };
          checkPending();
        }, 1000);
      }

      // Redirect to PayPal approval URL
      if (data.approvalUrl) {
        window.location.href = data.approvalUrl;
      } else {
        throw new Error("No approval URL received from PayPal");
      }
    } catch (err) {
      console.error("Error creating PayPal order:", err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      alert(`Payment Error: ${errorMsg}`);
      setProcessing(null);
    }
  };

  return (
    <>
      <div className="page-content bg-white">
        <PageTitle activePage={"Pricing"} parentTitle="Healthy Package Purchase" />

        {error && !error.includes('Payment pending') && (
          <div className="alert alert-danger text-center">
            <strong>Error:</strong> {error}
            <br />
            {(error.includes('capture') || error.includes('payment')) && (
              <button 
                onClick={handleManualCapture}
                className="btn btn-warning btn-sm mt-2"
                disabled={loading}
              >
                Complete Payment Manually
              </button>
            )}
          </div>
        )}

        {/* Show pending payment notice only if there's an actual pending order (not failed) */}
        {(() => {
          const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');
          // Backend only returns orders with status: 'pending', so if we have orders, they're pending
          // Also verify the orderId in localStorage exists in the pending orders list
          const orderInLocalStorageExists = pendingOrder.orderId && 
            pendingOrders.some(order => order.orderId === pendingOrder.orderId);
          const hasPending = (orderInLocalStorageExists || pendingOrders.length > 0) && !hasSubscription;
          
          if (hasPending) {
            return (
              <div className="alert alert-warning text-center">
                <strong>⚠ Payment Pending</strong>
                <br />
                <p className="mb-2">
                  You have a pending payment. You can either:
                </p>
                <div className="d-flex flex-column flex-md-row gap-2 justify-content-center align-items-center">
                  {pendingOrders.length > 0 ? (
                    <>
                      {pendingOrders.map((order, idx) => (
                        <div key={idx} className="mb-2">
                          <button 
                            onClick={() => handleManualCapture(order.orderId, order.packageId, order.plan || 'monthly')}
                            className="btn btn-primary btn-sm"
                            disabled={loading}
                          >
                            {loading ? "Processing..." : `Complete Payment `}
                          </button>
                        </div>
                      ))}
                    </>
                  ) : (
                    <button 
                      onClick={handleManualCapture}
                      className="btn btn-primary btn-sm"
                      disabled={loading}
                    >
                      {loading ? "Processing..." : "Complete Pending Payment"}
                    </button>
                  )}
                  <span className="mx-2">OR</span>
                  <span className="text-muted small">
                    Click "Subscribe Now" below to create a new payment attempt
                  </span>
                </div>
              </div>
            );
          }
          return null;
        })()}

       

        {promotion && isPromotionValid(promotion) && (
          <div className="alert alert-success text-center">
            🎉 Promotion: <strong>{promotion.value}% </strong> —{" "}
            <em>{promotion.type}</em>
          </div>
        )}

        <section
          className="content-inner rounded-shape-top overflow-hidden"
          style={{ backgroundImage: `url(${IMAGES.BgImage1})` }}
        >
          <div className="container">
            <div className="row">
              {plans.map((plan, ind) => (
                <div className="col-lg-4 col-md-6 m-b30" key={plan.id}>
                  <div
                    className={`pricingtable-wrapper box-hover style-1 ${
                      ind === hoverEffect ? "active" : ""
                    }`}
                    onMouseEnter={() => setHoverEffect(ind)}
                  >
                    <div className="pricingtable-inner">
                      <div className="pricingtable-title">{plan.title}</div>

                      <div className="pricingtable-price">
                        <h2 className="pricingtable-bx text-primary">
                          ${(plan.price ).toFixed(2)}
                          {plan.originalPrice && plan.originalPrice > plan.price && promotion && isPromotionValid(promotion) && (
                            <>
                              <br />
                              <small 
                                className="text-muted" 
                                style={{ 
                                  textDecoration: 'line-through', 
                                  fontSize: '0.7em', 
                                  display: 'block',
                                  marginTop: '5px',
                                  fontWeight: 'normal'
                                }}
                              >
                                ${(plan.originalPrice ).toFixed(2)}
                              </small>
                            </>
                          )}
                        </h2>
                        <p>{plan.description}</p>
                      </div>

                      <ul className="pricingtable-features">
                        {plan.features.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>

                      <div className="pricingtable-footer">
                        <button
                          onClick={() => handlePlanClick(plan.id)}
                          className="btn btn-primary btn-skew"
                          disabled={processing === plan.id || loading}
                        >
                          <span>
                            {processing === plan.id
                              ? "Processing..."
                                : (() => {
                                    const pendingOrder = JSON.parse(localStorage.getItem('pendingPayPalOrder') || '{}');
                                    // Only show "Retry Payment" if order exists in pending orders list (not failed)
                                    const orderExists = pendingOrder.orderId && 
                                      pendingOrders.some(order => order.orderId === pendingOrder.orderId);
                                    const hasPending = orderExists || pendingOrders.length > 0;
                                    return hasPending ? "Retry Payment" : "Subscribe Now";
                                  })()}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="call-action style-1 footer-action">
          <div className="container">
            <NewsLetter />
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing;
